/**
 * IntegrationSettingsPageV2 — cấu hình tích hợp DAT bên ngoài
 * (DTLX MOT_DAT, TP Ecotek, SLX Jira) cho trung tâm.
 *
 * Các cổng này cấp token JWT ngắn hạn qua đăng nhập có captcha nên KHÔNG
 * tự gia hạn được. Khi token hết hạn, admin đăng nhập lại trên cổng, copy
 * accessToken (hoặc dán nguyên JSON kết quả đăng nhập) rồi dán vào đây.
 *
 * BE: GET /v2/dat/integrations, PATCH /v2/dat/integrations/:id/token.
 */
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import datV2 from '../../services/datV2';
import V2PageLayout from '../../components/v2/V2PageLayout';

// Nhãn thân thiện cho từng tích hợp theo `name`.
const INTEGRATION_LABELS = {
  'dtlx-mot-dat': 'DTLX — Cục Đường bộ (MOT_DAT)',
  'ecotek-hanhtrinh': 'TP Ecotek — Hành trình GPS',
  'slx-jira-sessions': 'SLX Jira — Danh sách phiên',
  'slx-jira-mark': 'SLX Jira — Đánh dấu phiên',
};
const labelOf = (name) => INTEGRATION_LABELS[name] || name;

// Dán token có thể là JWT thô hoặc JSON kết quả đăng nhập
// ({ "accessToken": "...", "tokenType": "Bearer " }). Rút accessToken ra.
function extractToken(raw) {
  const t = (raw || '').trim();
  if (!t) return '';
  if (t.startsWith('{')) {
    try {
      const obj = JSON.parse(t);
      return obj.accessToken || obj.token || '';
    } catch (_) {
      return '';
    }
  }
  return t;
}

// Giải mã claim exp (giây) từ payload JWT để xem trước hạn dùng.
function decodeJwtExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? new Date(payload.exp * 1000) : null;
  } catch (_) {
    return null;
  }
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '—');

// Badge trạng thái hạn dùng.
function ExpiryBadge({ integration }) {
  const { tokenExpiredAt, isExpired, hasToken } = integration;
  if (!hasToken) return <Badge bg='secondary'>Chưa có token</Badge>;
  if (isExpired) return <Badge bg='danger'>Đã hết hạn</Badge>;
  if (tokenExpiredAt) {
    const msLeft = new Date(tokenExpiredAt).getTime() - Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    if (msLeft < dayMs) return <Badge bg='warning' text='dark'>Sắp hết hạn</Badge>;
    if (msLeft < 2 * dayMs) return <Badge bg='warning' text='dark'>Còn &lt; 2 ngày</Badge>;
  }
  return <Badge bg='success'>Còn hạn</Badge>;
}

export default function IntegrationSettingsPageV2() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal state
  const [editing, setEditing] = useState(null); // integration đang sửa
  const [rawInput, setRawInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await datV2.getIntegrations();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Không tải được danh sách tích hợp.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (integration) => {
    setEditing(integration);
    setRawInput('');
    setSaveError('');
  };
  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setRawInput('');
    setSaveError('');
  };

  const token = extractToken(rawInput);
  const previewExpiry = token ? decodeJwtExpiry(token) : null;
  const previewExpired = previewExpiry && previewExpiry.getTime() < Date.now();

  const submit = async () => {
    if (!token) {
      setSaveError('Không đọc được token. Dán JWT hoặc JSON kết quả đăng nhập có "accessToken".');
      return;
    }
    if (previewExpired) {
      setSaveError('Token này đã hết hạn. Vui lòng lấy token mới trên cổng.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await datV2.updateIntegrationToken(editing._id, token);
      setEditing(null);
      setRawInput('');
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Cập nhật token thất bại.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <V2PageLayout
      title={<>Cấu hình tích hợp DAT{' '}<Badge bg='light' text='dark'>{items.length}</Badge></>}
      actions={
        <Button size='sm' variant='outline-secondary' onClick={load} disabled={loading}>
          {loading ? <Spinner size='sm' animation='border' /> : 'Tải lại'}
        </Button>
      }
      gridClassName=''
    >
      {error && <Alert variant='danger' className='py-2'>{error}</Alert>}

      <Alert variant='info' className='py-2'>
        Hệ thống <strong>tự động gia hạn</strong> token DTLX (cửa sổ trượt 7 ngày) mỗi khi gọi API,
        nên bình thường không cần thao tác gì. Chỉ khi một tích hợp bị bỏ quá 7 ngày và báo
        <strong> Đã hết hạn</strong>, hãy đăng nhập lại trên cổng, sao chép <code>accessToken</code>
        {' '}(hoặc nguyên JSON kết quả đăng nhập) rồi bấm <strong>Cập nhật token</strong>.
      </Alert>

      <div style={{ overflowX: 'auto' }}>
        <Table hover bordered size='sm' className='align-middle bg-white'>
          <thead>
            <tr>
              <th>Tích hợp</th>
              <th>Trạng thái</th>
              <th>Hạn token</th>
              <th>Token (rút gọn)</th>
              <th>Endpoint</th>
              <th className='text-end'>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={6} className='text-center text-muted py-3'>Chưa có tích hợp nào.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it._id}>
                <td>
                  <div className='fw-semibold'>{labelOf(it.name)}</div>
                  <div className='text-muted small'>{it.name} · {it.authType}</div>
                </td>
                <td><ExpiryBadge integration={it} /></td>
                <td className='small'>{fmtDate(it.tokenExpiredAt)}</td>
                <td className='small'><code>{it.tokenPreview || '—'}</code></td>
                <td className='small text-truncate' style={{ maxWidth: 260 }} title={it.endpoint}>
                  {it.endpoint}
                </td>
                <td className='text-end'>
                  <Button size='sm' variant='primary' onClick={() => openEdit(it)}>
                    Cập nhật token
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Modal show={!!editing} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title className='fs-6'>
            Cập nhật token — {editing ? labelOf(editing.name) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className='small'>
              Dán <strong>accessToken</strong> (JWT) hoặc nguyên JSON kết quả đăng nhập:
            </Form.Label>
            <Form.Control
              as='textarea'
              rows={5}
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              placeholder='eyJhbGciOi... hoặc {"accessToken":"eyJ...","tokenType":"Bearer "}'
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              autoFocus
            />
          </Form.Group>

          {token && (
            <div className='small mt-2'>
              <div>Token đọc được: <code>{token.slice(0, 12)}…{token.slice(-8)}</code></div>
              <div>
                Hạn dùng:{' '}
                {previewExpiry ? (
                  <span className={previewExpired ? 'text-danger fw-semibold' : 'text-success'}>
                    {fmtDate(previewExpiry)} {previewExpired ? '(đã hết hạn)' : ''}
                  </span>
                ) : (
                  <span className='text-muted'>không đọc được exp</span>
                )}
              </div>
            </div>
          )}

          {saveError && <Alert variant='danger' className='py-2 mt-2 mb-0'>{saveError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={closeEdit} disabled={saving}>Huỷ</Button>
          <Button variant='primary' onClick={submit} disabled={saving || !token || previewExpired}>
            {saving ? <Spinner size='sm' animation='border' /> : 'Lưu token'}
          </Button>
        </Modal.Footer>
      </Modal>
    </V2PageLayout>
  );
}
